import React, { useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { NEX_INTRO_VIDEO } from "@/lib/assets";

/**
 * Middle-column hero card: 30-second NEX brand loop.
 * Auto-plays muted + loops forever so the hero always feels alive (all
 * modern browsers block autoplay unless the video is muted). Visitors
 * can tap the speaker toggle in the corner to unmute and hear the
 * commercial without ever stopping the loop.
 */
const HeroVideoCard = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    if (!next) {
      v.play().catch(() => {});
    }
    setMuted(next);
  };

  return (
    <div
      data-testid="hero-video-card"
      className="card-industrial corner-tick p-4 relative overflow-hidden scanline"
    >
      <div className="flex items-center justify-between mb-4">
        <span className="mono-label">// NEX_INTRO</span>
        <span className="flex items-center gap-2 mono-body text-[11px]">
          <span className="w-1.5 h-1.5 rounded-full bg-nex-gold animate-pulse-gold" />
          LIVE LOOP
        </span>
      </div>

      <div className="relative aspect-video w-full overflow-hidden bg-black">
        <video
          ref={videoRef}
          data-testid="hero-video-player"
          src={NEX_INTRO_VIDEO}
          poster="/nex-mark.png"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-full object-cover"
        />

        <button
          type="button"
          onClick={toggleMute}
          data-testid="hero-video-mute-toggle"
          aria-label={muted ? "Unmute NEX intro video" : "Mute NEX intro video"}
          aria-pressed={!muted}
          className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 bg-black/70 hover:bg-black/90 border border-nex-gold/50 hover:border-nex-gold text-nex-gold transition-all backdrop-blur-sm group"
        >
          {muted
            ? <VolumeX size={13} className="group-hover:scale-110 transition-transform" />
            : <Volume2 size={13} className="group-hover:scale-110 transition-transform" />}
          <span className="mono-body text-[9px] tracking-[0.2em] uppercase font-bold">
            {muted ? "Tap for sound" : "Sound on"}
          </span>
        </button>
      </div>

      <div className="metal-divider my-4" />
      <p className="mono-body text-[11px] leading-relaxed text-nex-chrome">
        &gt; <span className="text-nex-gold">Meet the engine</span> · a 30-second look at what NEX deploys for your business.
      </p>
    </div>
  );
};

export default HeroVideoCard;
